<template>
  <div class="vocabulary-book" v-if="!isVideoScreenLayout">
    <div class="header">
      <div>{{ title }}</div>
      <span @click="setShowVocabularyBook(false)"><mdicon name="close" size="24px"/></span>
    </div>

    <perfect-scrollbar v-if="!$_.isEmpty(vocabularyList)" class="scroll-bar">
      <div
        v-for="(item, index) in vocabularyList"
        :key="index"
        :class="`wordbook-list vocabulary-${item.word}`"
      >
        <div class="list">
          <div class="row">
            <div :ref="`vocabulary-container-${item.word}`" class="word">
              {{ item.word }}
            </div>
            <div class="action">
              <span @click="edit(item, index)">
                <mdicon :name="editIcon(index)" size="20px" />
              </span>
              <span @click="setImportant(item)">
                <mdicon :name="starIcon(item)" size="20px" />
              </span>
            </div>
          </div>

          <div class="text-line card">
            <div class="row-flex-end definition">
              <v-clamp autoresize :max-lines="4">
                {{ item.definition }}
              </v-clamp>
              <div class="expand" @click="showDictionaryTooltip($event, item, index)">
                <mdicon name="chevron-down" size="24px" />
                <DictionaryTooltip
                  v-if="tooltip === index"
                  class="tooltip-vocabulary"
                  :dictionary="dictionary"
                  @close="closeTooltip"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="edit-box" v-show="editVocabulary == index">
          <el-input type="textarea" v-model="editTextarea" maxlength="255"></el-input>
          <el-button class="btn-update" @click="saveNote">{{
            $t('vocabularyBook.update')
          }}</el-button>
          <el-button class="btn-cancel" type="text" @click="resetEditNote">{{
            $t('vocabularyBook.cancel')
          }}</el-button>
        </div>
      </div>
    </perfect-scrollbar>

    <NoWordsDisplay v-else />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapActions, mapState } from 'vuex';
import VClamp from 'vue-clamp';
import VocabularyTooltip from '@/mixins/vocabularyTooltip';

import NoWordsDisplay from '@/components/Lesson/Vocabulary/NoWordsDisplay';
import DictionaryTooltip from '@/components/Dictionary/Tooltip';
import { getApiMethod } from '@/modules/apiSelector';

export default {
  components: { VClamp, NoWordsDisplay, DictionaryTooltip },
  mixins: [VocabularyTooltip],
  data() {
    return {
      editVocabulary: null,
      editTextarea: null,
      editNoteId: null,
      dictionary: {},
      closeTooltipOnce: false, //workaround to not always call showVocabularytooltip when x is clicked
    };
  },
  computed: {
    ...mapState('vocabulary', ['categorizedWords']),
    ...mapState('material', ['articleId']),
    ...mapState('htmlMaterial', ['htmlLessonId']),
    ...mapGetters('layout', ['isVideoScreenLayout']),
    ...mapGetters(['isDiscoveryLesson']),

    vocabularyList() {
      if (this.$_.isNull(this.categorizedWords)) {
        return [];
      }

      return this.$_.map(this.categorizedWords.hits, item => {
        const word = this.$_.lowerCase(item.word);
        return {
          id: item.id,
          translation: item.translation,
          word,
          definition: !this.$_.isEmpty(item.definition)
            ? item.definition[0].meaning.join('\r\n')
            : '',
          isImportant: item.is_important,
          isMemorized: item.is_memorized,
          notes: item.notes,
          program_id: item.program_id,
          lesson_id: item.lesson_id,
        };
      });
    },

    title() {
      return !this.$_.isEmpty(this.vocabularyList)
        ? this.$t('vocabularyBook.title')
        : this.$t('vocabularyBook.noWordTitle');
    },
  },
  async mounted() {
    await this.fetchCategorizedWords();
    window.addEventListener('resize', this.hideTooltipOnResize);
  },
  methods: {
    ...mapMutations('material', ['setShowVocabularyBook']),
    ...mapMutations('vocabulary', ['setAppStar', 'setWordNote']),
    ...mapActions('vocabulary', ['setAsImportant']),

    edit(item, index) {
      const { id, notes } = item;
      this.editVocabulary = this.editVocabulary === index ? null : index;
      this.editTextarea = notes;
      this.editNoteId = id;
    },

    resetEditNote() {
      this.editVocabulary = null;
      this.editNoteId = null;
    },

    async saveNote() {
      const params = {
        id: this.editNoteId,
        note: this.editTextarea,
      };

      const apiMethod = getApiMethod('updateVocabularyNote');
      await apiMethod(params);

      await this.$notify({
        message: this.$t('vocabulary.noteUpdated'),
        type: 'success',
        position: 'bottom-left',
        duration: 3000,
        customClass: 'vocabulary-notification',
        showClose: false,
      });

      this.setWordNote(params);
      this.resetEditNote();
    },

    async setImportant(item) {
      const { id, isImportant } = item;
      const important = +!isImportant;
      const params = { id, important };
      const apiMethod = getApiMethod('setImportantData');
      await apiMethod(params);
      this.setAppStar(params);
    },

    editIcon(index) {
      return this.editVocabulary === index ? 'pencil' : 'pencil-outline';
    },

    starIcon(item) {
      return item.isImportant ? 'star' : 'star-outline';
    },

    async showDictionaryTooltip(evt, item, index) {
      if (this.closeTooltipOnce) {
        return;
      }

      const params = {
        word: item.word,
        lesson_id: item.lesson_id,
        program_id: item.program_id,
      };

      try {
        this.dictionary = await this.checkDictionaryData(params);
        this.tooltip = index;

        this.$nextTick(() => {
          this.setTooltipPosition(evt);
          document.addEventListener('click', this.setTooltipHide);

          document.querySelector('.tooltip-content').addEventListener('click', e => {
            // Added this to stop clicking inside tooltip and open a new tooltip
            e.stopPropagation();
          });
        });
      } catch (error) {
        console.log(error);
      }
    },

    async checkDictionaryData(params) {
      let result = null;
      const apiMethod = getApiMethod('getTranslation');
      result = await apiMethod(params.word, params.lesson_id, params.program_id);
      return result;
    },

    setTooltipPosition(evt) {
      const elPosition = this.getOffset(evt.srcElement);
      this.element = document.querySelector('.tooltip-content');

      if (this.element) {
        const visibleInWindow = window.innerWidth - elPosition.left >= 310;
        this.element.style.left = visibleInWindow
          ? elPosition.left - 70 + 'px'
          : window.innerWidth - 300 + 'px';

        if (elPosition.top > 400) {
          this.element.style.top = elPosition.top - 320 + 'px';
        } else {
          this.element.style.top = elPosition.top + 20 + 'px';
        }
      }
    },

    closeTooltip() {
      this.hideTooltip();
      this.closeTooltipOnce = true;
      this.element = null;

      setTimeout(() => {
        this.closeTooltipOnce = false;
      }, 10);
    },

    hideTooltipOnResize() {
      const tooltipElement = document.querySelector('.tooltip-content');
      if (!tooltipElement) {
        return;
      }

      if (window.innerWidth < 960) {
        this.closeTooltip();
      }
    },

    getOffset(el) {
      const rect = el.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top,
      };
    },
  },
  beforeDestroy: function() {
    window.removeEventListener('resize', this.hideTooltipOnResize);
  },
};
</script>

<style scoped src="@/assets/css/pc/vocabulary_book.css"></style>
<style scoped src="@/assets/css/pc/vocabulary_tooltip.css"></style>
<style scoped>
.expand {
  cursor: pointer;
}

@media screen and (max-width: 959px) {
  .vocabulary-book {
    display: none;
  }
}
</style>
